/** RBAC role codes — 0-based, mirrors backend RoleCode. */
export const RoleCode = {
  SUPER_ADMIN: 0,
  FINANCIAL_ADVISOR: 1,
  HR_MANAGER: 2,
  SALES: 3,
  DESKTOP_AGENT: 4,
  AI_AGENT: 5,
} as const;

export type RoleCodeValue = (typeof RoleCode)[keyof typeof RoleCode];

export const ROLE_LABELS: Record<RoleCodeValue, string> = {
  [RoleCode.SUPER_ADMIN]: "SuperAdmin",
  [RoleCode.FINANCIAL_ADVISOR]: "FinancialAdvisor",
  [RoleCode.HR_MANAGER]: "HRManager",
  [RoleCode.SALES]: "Sales",
  [RoleCode.DESKTOP_AGENT]: "DesktopAgent",
  [RoleCode.AI_AGENT]: "AIAgent",
};

/** Enterprise-facing role labels used in route guards. */
export const EnterpriseRole = {
  SuperAdmin: "SuperAdmin",
  FinancialAdvisor: "FinancialAdvisor",
  HRManager: "HRManager",
  InventoryManager: "InventoryManager",
  SalesAgent: "SalesAgent",
} as const;

export type EnterpriseRoleName =
  (typeof EnterpriseRole)[keyof typeof EnterpriseRole];

const ENTERPRISE_ROLE_TO_CODE: Record<EnterpriseRoleName, RoleCodeValue> = {
  [EnterpriseRole.SuperAdmin]: RoleCode.SUPER_ADMIN,
  [EnterpriseRole.FinancialAdvisor]: RoleCode.FINANCIAL_ADVISOR,
  [EnterpriseRole.HRManager]: RoleCode.HR_MANAGER,
  [EnterpriseRole.InventoryManager]: RoleCode.DESKTOP_AGENT,
  [EnterpriseRole.SalesAgent]: RoleCode.SALES,
};

export function resolveEnterpriseRoles(
  allowedRoles: string[],
): RoleCodeValue[] {
  const resolved: RoleCodeValue[] = [];

  for (const rawName of allowedRoles) {
    const name = rawName.trim() as EnterpriseRoleName;
    const code = ENTERPRISE_ROLE_TO_CODE[name];
    if (code !== undefined && !resolved.includes(code)) {
      resolved.push(code);
    }
  }

  return resolved;
}

export const FinanceAdminRoles: RoleCodeValue[] = [
  RoleCode.SUPER_ADMIN,
  RoleCode.FINANCIAL_ADVISOR,
];

export const HrAdminRoles: RoleCodeValue[] = [
  RoleCode.SUPER_ADMIN,
  RoleCode.HR_MANAGER,
];

export const InventoryAdminRoles: RoleCodeValue[] = [
  RoleCode.SUPER_ADMIN,
  RoleCode.DESKTOP_AGENT,
  RoleCode.SALES,
];

export const SalesAdminRoles: RoleCodeValue[] = [
  RoleCode.SUPER_ADMIN,
  RoleCode.SALES,
];

export const ROLE_OPTIONS: Array<{ value: RoleCodeValue; label: string }> =
  Object.entries(ROLE_LABELS).map(([value, label]) => ({
    value: Number(value) as RoleCodeValue,
    label,
  }));

export function hasRole(
  roles: RoleCodeValue[],
  ...required: RoleCodeValue[]
): boolean {
  return required.some((r) => roles.includes(r));
}

export function hasAnyRole(
  roles: RoleCodeValue[],
  allowed: RoleCodeValue[],
): boolean {
  return allowed.some((role) => roles.includes(role));
}

export function hasAllRoles(
  roles: RoleCodeValue[],
  ...required: RoleCodeValue[]
): boolean {
  return required.every((r) => roles.includes(r));
}
